import { PageHeading } from "@via/components/page-heading.tsx";
import useUrlParams from "@via/hooks/useURLParams";
import {
  BUTTONS,
  ERROR,
  PAGE_NAMES,
  PREMIUM_DATASET_TABLE,
  ROUTES,
} from "@via/utils/constants.tsx";
import { useCallback, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  useDeleteDataset,
  usePremiumDatasetList,
} from "@via/pages/data/premium-dataset/premium-dataset.api.tsx";
import PremiumDatasetTable from "@via/pages/data/premium-dataset/components/premium-dataset-table.tsx";
import { TableContextMenuInterface } from "@via/utils/interfaces";
import { GenericError } from "@via/components/error";
import { DrawerSize } from "@via/components/table/table";
import DeleteModal from "@via/components/delete-modal/delete-modal";
import { TaggableEntityType, VmetaDataSetResponse } from "common";
import TagValueInput from "@via/components/tag-value-input.tsx";
import { ActionButton } from "@via/components/action-button/action-button";
import Drawer from "@via/components/drawer";
import CreatePremiumDatasetForm from "@via/pages/data/premium-dataset/components/create-premium-dataset-form.tsx";
import EditPremiumDatasetForm from "@via/pages/data/premium-dataset/components/edit-premium-dataset-form.tsx";
import CopyToCloudModal from "@via/pages/data/premium-dataset/components/copy-to-cloud-modal.tsx";
import { useRegisterCortexContext } from "@via/hooks/useRegisterCortexContext";
import { useAuth } from "@via/hooks/useAuth";
import { PremiumDatasetViewType } from "@via/pages/data/data-collections/components/data-collection-filter";

const defaultParams = {
  sort: [{ id: "updatedAt", desc: true }],
  filters: [],
  page: 0,
  pageSize: 10,
};

export default function PremiumDatasetList(): JSX.Element {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  const { setUrlParams, getInitialParams } = useUrlParams(defaultParams);
  const initialParams = getInitialParams();

  const [page, setPage] = useState(initialParams.page);
  const [pageSize, setPageSize] = useState(initialParams.pageSize);
  const [sort, setSort] = useState<Array<{ id: string; desc: boolean }>>(
    initialParams.sort
  );
  const [columnFilters, setColumnFilters] = useState<
    Array<{ id: string; value: unknown }>
  >(() => (initialParams.filters || []).filter(f => f.id !== "viewType"));
  const [premiumDatasetViewType, setPremiumDatasetViewType] =
    useState<PremiumDatasetViewType>(PremiumDatasetViewType.My);
  const [datasetToBeDeleted, setDatasetToBeDeleted] =
    useState<VmetaDataSetResponse | null>(null);
  const [datasetToBeEdited, setDatasetToBeEdited] =
    useState<VmetaDataSetResponse | null>(null);
  const [datasetToCopyToCloud, setDatasetToCopyToCloud] =
    useState<VmetaDataSetResponse | null>(null);
  const [isDrawerOpen, setDrawerOpen] = useState(false);

  // Check if user came from navbar "New Dataset" button and open drawer
  useEffect(() => {
    const shouldOpenDrawer = searchParams.get("new") === "true";
    if (shouldOpenDrawer) {
      setDrawerOpen(true);
      // Remove the query parameter from URL
      searchParams.delete("new");
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const handleSetColumnFilters = (
    filters: (
      prevFilters: Array<{ id: string; value: unknown }>
    ) => Array<{ id: string; value: unknown }>
  ) => {
    setColumnFilters(prev => filters(prev).filter(f => f.id !== "viewType"));
  };

  const [tagFilter, setTagFilter] = useState<string>("");
  const { data, isLoading, isError } = usePremiumDatasetList(
    page,
    pageSize,
    sort,
    columnFilters,
    true,
    premiumDatasetViewType,
    user?.vmeta?.userId,
    tagFilter ? [tagFilter] : []
  );
  const { mutateAsync: deleteDataset, isPending: isDeleteDatasetLoading } =
    useDeleteDataset();

  useEffect(() => {
    setUrlParams({
      sort,
      filters: columnFilters,
      page,
      pageSize,
    });
  }, [sort, columnFilters, page, pageSize]);

  const getPremiumDatasetListContext = useCallback(() => {
    const datasets = data?.data || [];

    return {
      datasets: datasets.map(dataset => ({
        id: dataset._id,
        name: dataset.name,
        fileCount: dataset.fileCount,
        createdAt: dataset.createdAt,
        updatedAt: dataset.updatedAt,
        owner: dataset.owner?.name,
      })),
      total: data?.results || 0,
      currentPage: page,
      pageSize: pageSize,
      filters: columnFilters,
      sortBy: sort.length > 0 ? sort[0] : null,
    };
  }, [data?.data, data?.results, columnFilters, page, pageSize, sort]);

  useRegisterCortexContext({
    routePattern: ROUTES.PREMIUM_COLLECTIONS,
    contextName: "Premium Dataset List",
    getContext: getPremiumDatasetListContext,
    systemPrompt:
      "You are analyzing premium dataset list page. Help answer questions about the premium dataset list page.",
  });

  const handleAction = (
    option: TableContextMenuInterface,
    dataset: VmetaDataSetResponse
  ): void => {
    switch (option.name) {
      case PREMIUM_DATASET_TABLE[0].name:
        navigate(`/data/collections/${dataset._id}`);
        break;
      case PREMIUM_DATASET_TABLE[1].name:
        setDatasetToBeEdited(dataset);
        break;
      case PREMIUM_DATASET_TABLE[2].name:
        setDatasetToCopyToCloud(dataset);
        break;
      case PREMIUM_DATASET_TABLE[3].name:
        setDatasetToBeDeleted(dataset);
        break;
      default:
        break;
    }
  };

  return (
    <main className="w-full">
      <div className="via-page-heading-wrapper">
        <PageHeading title={PAGE_NAMES.DATASETS} />
        <ActionButton
          button={BUTTONS.CREATE_DATASET}
          primary={true}
          onClick={() => {
            setDrawerOpen(true);
          }}
        />
      </div>
      {isError ? (
        <div className="via-generic-error">
          <GenericError message={ERROR} />
        </div>
      ) : (
        <div>
          <div className="w-64 mb-3">
            <TagValueInput
              entityType={TaggableEntityType.Dataset}
              value={tagFilter}
              onChange={value => {
                setTagFilter(value);
                setPage(0);
              }}
            />
          </div>
          <PremiumDatasetTable
            data={data?.data}
            isLoading={isLoading}
            page={page}
            setPage={setPage}
            pageSize={pageSize}
            setPageSize={setPageSize}
            sort={sort}
            setSort={setSort}
            columnFilters={columnFilters}
            setColumnFilters={handleSetColumnFilters}
            premiumDatasetViewType={premiumDatasetViewType}
            onPremiumDatasetViewTypeChange={setPremiumDatasetViewType}
            total={data?.results}
            mode="management"
            onAction={handleAction}
          />
          <DeleteModal
            item={datasetToBeDeleted}
            setItem={setDatasetToBeDeleted}
            deleteFunction={() =>
              deleteDataset(datasetToBeDeleted!._id.toString())
            }
            isLoading={isDeleteDatasetLoading}
            title="Delete Dataset"
          />
          <CopyToCloudModal
            dataset={datasetToCopyToCloud}
            onClose={() => setDatasetToCopyToCloud(null)}
          />
        </div>
      )}
      <Drawer
        id="create-dataset"
        open={isDrawerOpen}
        setOpen={setDrawerOpen}
        size={DrawerSize.Big}>
        <CreatePremiumDatasetForm setOpen={setDrawerOpen} />
      </Drawer>
      <Drawer
        id="edit-dataset"
        open={!!datasetToBeEdited}
        setOpen={() => setDatasetToBeEdited(null)}
        size={DrawerSize.Big}>
        <EditPremiumDatasetForm
          setOpen={() => setDatasetToBeEdited(null)}
          dataset={datasetToBeEdited}
        />
      </Drawer>
    </main>
  );
}
